import React, { useEffect, useRef } from "react";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import { useDumont } from "../../context/DumontContext";
import { useAppState } from "../../context/StateContext";

interface DumontAuthGateProps {
  children: React.ReactNode;
}

/**
 * Runs client-exchange when there is no Dumont token, then renders children.
 * Must be used inside DumontProvider.
 */
export function DumontAuthGate({ children }: DumontAuthGateProps) {
  const { token, isLoading, error, exchange, baseUrl, clearToken } = useDumont();
  const { currentUser } = useAppState();
  const email = currentUser?.email ?? "";
  const displayName = [currentUser?.name, currentUser?.lastName].filter(Boolean).join(" ");
  const hasTriggeredExchange = useRef(false);

  useEffect(() => {
    if (!baseUrl || !email || token) return;
    if (hasTriggeredExchange.current) return;
    hasTriggeredExchange.current = true;
    exchange({ email, name: displayName || undefined });
  }, [baseUrl, email, displayName, token, exchange]);

  if (token) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          flex: 1,
          minHeight: 200,
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Connecting to Dumont…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          flex: 1,
          minHeight: 200,
          px: 2,
        }}
      >
        <Typography variant="body2" color="error" textAlign="center">
          {error}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            hasTriggeredExchange.current = false;
            clearToken();
            exchange({ email, name: displayName || undefined });
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        minHeight: 200,
      }}
    >
      <CircularProgress size={32} />
    </Box>
  );
}